import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { EmailTagInput } from '~/components/Shared/EmailTagInput/EmailTagInput';
import { Button, MultipleSelect, SelectBar } from '~/ui';
import styles from './EmailRoutingTab.module.scss';

interface EmailRoutingSetting {
  id?: number;
  entity_type: 'ticket' | 'feedback';
  scope_type: 'accommodation' | 'category' | 'food_object';
  scope_id: number | null;
  emails: unknown;
}

interface Category {
  id: number;
  name?: string;
  category?: string;
}

interface FoodPerson {
  id: number;
  name: string;
  patronymic: string;
  email: string;
  position: string;
  food_id: number;
}

interface FoodItem {
  id: number;
  name: string;
  food_persons: FoodPerson[];
}

interface TicketRule {
  _key: number;
  category_id: number | null;
  emails: string[];
}

interface FeedbackRule {
  _key: number;
  category_id: number | null;
  food_id: number | null;
  person_ids: number[];
}

interface EmailRoutingTabProps {
  accommodationId: string | undefined;
}

let ruleIdCounter = 0;

export const EmailRoutingTab = ({ accommodationId }: EmailRoutingTabProps) => {
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const [ticketRules, setTicketRules] = useState<TicketRule[]>([]);
  const [feedbackRules, setFeedbackRules] = useState<FeedbackRule[]>([]);

  const { data: existingSettings, isLoading: isLoadingSettings } = useQuery<
    EmailRoutingSetting[]
  >({
    queryFn: async () => {
      const response = await instance.get(
        `admin/accommodations/${accommodationId}/email-routing`
      );
      return response.data;
    },
    queryKey: ['email-routing', accommodationId],
    enabled: !!accommodationId,
    onError: error => {
      toastError(error);
    },
  });

  const { data: ticketCategories } = useQuery<Category[]>({
    queryFn: async () => {
      const response = await instance.get('admin/ticket-categories');
      return response.data;
    },
    queryKey: ['ticket-categories'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: feedbackCategories } = useQuery<Category[]>({
    queryFn: async () => {
      const response = await instance.get('admin/feedback-categories');
      return response.data.categories ?? response.data;
    },
    queryKey: ['feedback-categories'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: foods } = useQuery<FoodItem[]>({
    queryFn: async () => {
      const response = await instance.get(
        `admin/accommodations/${accommodationId}/foods`
      );
      return response.data;
    },
    queryKey: ['accommodation-foods', accommodationId],
    enabled: !!accommodationId,
    onError: error => {
      toastError(error);
    },
  });

  useEffect(() => {
    if (!existingSettings) return;

    const tickets: TicketRule[] = [];
    const feedbacks: FeedbackRule[] = [];

    existingSettings.forEach(s => {
      if (s.entity_type === 'ticket') {
        if (s.scope_type === 'accommodation' || s.scope_type === 'category') {
          tickets.push({
            _key: ++ruleIdCounter,
            category_id: s.scope_type === 'accommodation' ? null : s.scope_id,
            emails: s.emails as string[],
          });
        }
      }

      if (s.entity_type === 'feedback') {
        if (s.scope_type === 'accommodation') {
          feedbacks.push({
            _key: ++ruleIdCounter,
            category_id: null,
            food_id: null,
            person_ids: [],
          });
          // accommodation-level feedback emails не used in new format, skip
        }

        if (s.scope_type === 'food_object' && s.scope_id) {
          const data = s.emails as {
            rules?: { category_id: number | null; person_ids: number[] }[];
          };

          (data.rules ?? []).forEach(r => {
            feedbacks.push({
              _key: ++ruleIdCounter,
              category_id: r.category_id,
              food_id: s.scope_id,
              person_ids: r.person_ids ?? [],
            });
          });
        }
      }
    });

    // Remove empty accommodation feedback entries from old format
    const cleanedFeedbacks = feedbacks.filter(
      r => r.food_id !== null || r.person_ids.length > 0
    );

    setTicketRules(tickets);
    setFeedbackRules(cleanedFeedbacks);
  }, [existingSettings]);

  // --- Ticket rules ---
  const addTicketRule = () => {
    setTicketRules(prev => [
      ...prev,
      { _key: ++ruleIdCounter, category_id: null, emails: [] },
    ]);
  };

  const removeTicketRule = (key: number) => {
    setTicketRules(prev => prev.filter(r => r._key !== key));
  };

  const updateTicketRule = (key: number, field: string, value: unknown) => {
    setTicketRules(prev =>
      prev.map(r => (r._key === key ? { ...r, [field]: value } : r))
    );
  };

  // --- Feedback rules ---
  const addFeedbackRule = () => {
    setFeedbackRules(prev => [
      ...prev,
      {
        _key: ++ruleIdCounter,
        category_id: null,
        food_id: null,
        person_ids: [],
      },
    ]);
  };

  const removeFeedbackRule = (key: number) => {
    setFeedbackRules(prev => prev.filter(r => r._key !== key));
  };

  const updateFeedbackRule = (key: number, field: string, value: unknown) => {
    setFeedbackRules(prev =>
      prev.map(r => {
        if (r._key !== key) return r;
        const updated = { ...r, [field]: value };
        if (field === 'food_id') updated.person_ids = [];
        return updated;
      })
    );
  };

  const getPersonsForFood = useCallback(
    (foodId: number | null): FoodPerson[] => {
      if (!foodId || !foods) return [];
      return foods.find(f => f.id === foodId)?.food_persons ?? [];
    },
    [foods]
  );

  // --- Save ---
  const { mutateAsync: saveSettings, isLoading: isSaving } = useMutation(
    async () => {
      const payload: Record<string, unknown>[] = [];

      // Ticket rules → accommodation/category scope
      ticketRules.forEach(rule => {
        if (rule.emails.length === 0) return;
        payload.push({
          entity_type: 'ticket',
          scope_type: rule.category_id === null ? 'accommodation' : 'category',
          scope_id: rule.category_id,
          emails: rule.emails,
        });
      });

      // Feedback rules → group by food_id
      const foodRulesMap: Record<
        number,
        { category_id: number | null; person_ids: number[] }[]
      > = {};

      feedbackRules.forEach(rule => {
        if (!rule.food_id || rule.person_ids.length === 0) return;
        if (!foodRulesMap[rule.food_id]) foodRulesMap[rule.food_id] = [];
        foodRulesMap[rule.food_id].push({
          category_id: rule.category_id,
          person_ids: rule.person_ids,
        });
      });

      Object.entries(foodRulesMap).forEach(([foodIdStr, rules]) => {
        payload.push({
          entity_type: 'feedback',
          scope_type: 'food_object',
          scope_id: Number(foodIdStr),
          rules,
        });
      });

      const response = await instance.put(
        `admin/accommodations/${accommodationId}/email-routing`,
        { settings: payload }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('email_routing_saved'));
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  if (isLoadingSettings) {
    return null;
  }

  const ticketCategoryOptions = [
    { id: 'all', name: t('email_routing_all_categories') },
    ...(ticketCategories || []).map(c => ({
      id: String(c.id),
      name: c.category || c.name || `#${c.id}`,
    })),
  ];

  const feedbackCategoryOptions = [
    { id: 'all', name: t('email_routing_all_categories') },
    ...(feedbackCategories || []).map(c => ({
      id: String(c.id),
      name: c.name || `#${c.id}`,
    })),
  ];

  const foodOptions = (foods || []).map(f => ({
    id: String(f.id),
    name: f.name,
  }));

  return (
    <div className={styles.container}>
      {/* === Заявки === */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('email_routing_tickets')}</h3>

        <div className={styles.rulesList}>
          {ticketRules.map(rule => (
            <div key={rule._key} className={styles.ruleCard}>
              <div className={styles.ruleFieldsTicket}>
                <SelectBar
                  name="ticket_category"
                  label={t('email_routing_category_label')}
                  options={ticketCategoryOptions}
                  value={
                    rule.category_id === null ? 'all' : String(rule.category_id)
                  }
                  onChange={(val: string) =>
                    updateTicketRule(
                      rule._key,
                      'category_id',
                      val === 'all' ? null : Number(val)
                    )
                  }
                  el="name"
                  star={false}
                  disabled={false}
                  className={styles.selectNoMargin}
                />

                <EmailTagInput
                  label="Email"
                  emails={rule.emails}
                  onChange={emails =>
                    updateTicketRule(rule._key, 'emails', emails)
                  }
                />
              </div>

              <button
                type="button"
                className={styles.ruleRemoveBtn}
                onClick={() => removeTicketRule(rule._key)}
                title={t('delete')}
              >
                &times;
              </button>
            </div>
          ))}

          <Button
            text={t('email_routing_add_rule')}
            onClick={addTicketRule}
            buttonStyle="empty"
            className={styles.addRuleBtn}
          />
        </div>
      </div>

      {/* === Обратная связь === */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('email_routing_feedback')}</h3>

        <div className={styles.rulesList}>
          {feedbackRules.map(rule => {
            const persons = getPersonsForFood(rule.food_id);

            return (
              <div key={rule._key} className={styles.ruleCard}>
                <div className={styles.ruleFieldsFeedback}>
                  <SelectBar
                    name="feedback_category"
                    label={t('email_routing_category_label')}
                    options={feedbackCategoryOptions}
                    value={
                      rule.category_id === null
                        ? 'all'
                        : String(rule.category_id)
                    }
                    onChange={(val: string) =>
                      updateFeedbackRule(
                        rule._key,
                        'category_id',
                        val === 'all' ? null : Number(val)
                      )
                    }
                    el="name"
                    star={false}
                    disabled={false}
                    className={styles.selectNoMargin}
                  />

                  <SelectBar
                    name="food"
                    label={t('email_routing_food_object_label')}
                    options={foodOptions}
                    value={rule.food_id ? String(rule.food_id) : ''}
                    onChange={(val: string) =>
                      updateFeedbackRule(rule._key, 'food_id', Number(val))
                    }
                    el="name"
                    star={false}
                    disabled={false}
                    className={styles.selectNoMargin}
                  />

                  <MultipleSelect
                    label={t('email_routing_persons')}
                    options={persons.map(p => ({
                      id: String(p.id),
                      name: `${p.name} ${p.patronymic}`,
                      email: p.email,
                    }))}
                    value={rule.person_ids}
                    onChange={(ids: number[]) =>
                      updateFeedbackRule(rule._key, 'person_ids', ids)
                    }
                    star={false}
                    el="name"
                    secondEl="email"
                    disabled={!rule.food_id}
                    mode="multiple"
                  />
                </div>

                <button
                  type="button"
                  className={styles.ruleRemoveBtn}
                  onClick={() => removeFeedbackRule(rule._key)}
                  title={t('delete')}
                >
                  &times;
                </button>
              </div>
            );
          })}

          <Button
            text={t('email_routing_add_rule')}
            onClick={addFeedbackRule}
            buttonStyle="empty"
            className={styles.addRuleBtn}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          text={t('save')}
          onClick={() => saveSettings()}
          disabled={isSaving}
          isLoading={isSaving}
        />
      </div>
    </div>
  );
};
